import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, uploadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canManage, isCleaner } from "@/lib/perms";
import { StatusBadge } from "@/components/Badges";
import { label, money } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import IssueDialog from "@/components/IssueDialog";
import JobEditDialog from "@/components/JobEditDialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ImagePlus, Clock, MapPin, UserCheck, PlayCircle, CheckCircle2 } from "lucide-react";
import { WeatherLine } from "@/components/WeatherPanel";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const editable = canManage(user) || (isCleaner(user) && (job?.cleaner_ids || [job?.cleaner_id]).includes(user?._id));
  const [details, setDetails] = useState({});
  useEffect(() => { if (job) setDetails(job.details || {}); }, [job]);

  const load = useCallback(
    () =>
      api.get(`/jobs/${id}`).then(({ data }) => {
        setJob(data);
        setNotes(data.completion_notes || "");
      }),
    [id]
  );

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (body, msg) => {
    try {
      const { data } = await api.put(`/jobs/${id}`, body);
      setJob(data);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const toggleTask = (idx) => {
    const checklist = job.checklist.map((t, i) => (i === idx ? { ...t, done: !t.done } : t));
    setJob({ ...job, checklist });
    patch({ checklist });
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      await patch({ photos: [...(job.photos || []), ...paths] }, "Photos added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };

  if (!job) return <Loader2 className="h-6 w-6 animate-spin text-royal" />;

  const done = job.checklist.filter((t) => t.done).length;
  const pct = job.checklist.length ? Math.round((done / job.checklist.length) * 100) : 0;
  const spec = job.field_spec || [];
  const emergency = job.work_type === "emergency";
  const saveDetails = () => patch({ details }, "Job notes saved");
  const sections = [...new Set(job.checklist.map((t) => t.section))];

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <Link to="/schedule" data-testid="back-to-schedule" className="inline-flex items-center gap-1.5 text-sm text-royal hover:text-gold transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> Back to schedule
      </Link>

      <div className={`rounded-lg p-6 sm:p-8 ${emergency ? "bg-red-700 border-2 border-red-400" : "bg-royal-deep sidebar-grain"}`}>
        {emergency && (
          <p data-testid="emergency-banner" className="mb-3 font-display text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-white animate-pulse">
            EMERGENCY · TOP PRIORITY · IMMEDIATE RESPONSE REQUIRED
          </p>
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{job.work_type_label}</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mt-2">{job.title}</h1>
            <p className="text-blue-100/80 text-sm mt-1">{job.property_name}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm text-blue-100/80">
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-gold" /> {job.scheduled_date} at {job.scheduled_time}</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gold" /> {job.property_address}</span>
              <span className="flex items-center gap-1.5"><UserCheck className="h-4 w-4 text-gold" /> {(job.crew_names || []).join(", ") || "Unassigned"}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge value={job.status} testId="job-detail-status" />
            <span className="text-[11px] text-blue-200/70">Priority: {label(job.priority)}</span>
            {job.cost > 0 && (
              <span className="text-[11px] text-blue-200/70" data-testid="job-detail-cost">
                Cost: {money(job.cost)}{job.billable ? "" : " (not billable)"}
              </span>
            )}
            {job.invoice_id && (
              <span className="text-[11px] text-gold font-semibold" data-testid="job-detail-invoiced">Invoiced</span>
            )}
            {job.inspection && (
              <span className="text-[11px] text-blue-200/70" data-testid="job-detail-inspection">
                Inspection: {job.inspection.passed ? "passed" : "failed"} ({job.inspection.overall_score})
              </span>
            )}
          </div>
        </div>
      </div>

      {editable && (
        <div className="flex flex-wrap gap-3">
          {(job.next_statuses || []).map((next) => (
            <Button
              key={next}
              data-testid={`transition-${next}-btn`}
              onClick={() => {
                if (next === "completed") {
                  const missing = spec.filter(
                    (f) => f.required_to_complete && !String(details[f.key] || "").trim()
                  );
                  if (missing.length) {
                    toast.error(`Fill "${missing[0].label}" before completing this work order.`);
                    return;
                  }
                }
                patch(
                  next === "completed" || next === "awaiting_inspection"
                    ? { status: next, completion_notes: notes, ...(spec.length ? { details } : {}) }
                    : { status: next },
                  `Moved to ${label(next)}`
                );
              }}
              className={
                next === "completed" || next === "closed"
                  ? "bg-gold hover:bg-gold/90 text-royal-deep font-semibold gap-2"
                  : "bg-royal hover:bg-royal-deep text-white gap-2"
              }
            >
              {next === "in_progress" ? <PlayCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              {next === "in_progress" ? "Start work" : `Move to ${label(next)}`}
            </Button>
          ))}
          <IssueDialog propertyId={job.property_id} jobId={job._id} onCreated={load} />
          <div className="w-full"><WeatherLine propertyId={job.property_id} /></div>
          {canManage(user) && (
            <JobEditDialog
              job={job}
              onSaved={(data) => {
                setJob(data);
                setNotes(data.completion_notes || "");
              }}
            />
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
        {spec.length > 0 && (
          <div className={`rounded-lg border bg-white p-5 sm:p-6 ${emergency ? "border-red-400" : "border-border"}`}
               data-testid="job-fields-card">
            <h2 className={`font-display text-lg font-semibold ${emergency ? "text-red-700 uppercase tracking-wide" : "text-royal-deep"}`}>
              {emergency ? "EMERGENCY DETAILS" : `${job.work_type_label} details`}
            </h2>
            <div className="mt-4 space-y-4">
              {spec.map((f) => (
                <div key={f.key}>
                  <Label className={`text-xs uppercase tracking-wider ${emergency ? "text-red-700 font-bold" : "text-muted-foreground"}`}>
                    {f.label}{f.required_to_complete ? " *" : ""}
                  </Label>
                  {f.hint && <p className="text-[11px] text-muted-foreground mt-0.5">{f.hint}</p>}
                  {f.long ? (
                    <Textarea
                      data-testid={`job-field-${f.key}`}
                      className="mt-1.5 min-h-[84px]"
                      value={details[f.key] || ""}
                      disabled={!editable}
                      onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })}
                    />
                  ) : (
                    <input
                      data-testid={`job-field-${f.key}`}
                      className="mt-1.5 w-full rounded-md border border-border px-3 py-2 text-sm"
                      value={details[f.key] || ""}
                      disabled={!editable}
                      onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })}
                    />
                  )}
                </div>
              ))}
            </div>
            {editable && (
              <Button data-testid="save-job-fields-btn" onClick={saveDetails}
                      className={`mt-4 ${emergency ? "bg-red-700 hover:bg-red-800 text-white" : "bg-royal hover:bg-royal-deep text-white"}`}>
                Save details
              </Button>
            )}
            {job.escalated_work_order_id && (
              <p className="mt-3 text-xs text-royal" data-testid="escalated-note">
                Issues found raised a tracked issue and a{" "}
                <Link to={`/jobs/${job.escalated_work_order_id}`} className="font-semibold underline">work order to fix it</Link>.
              </p>
            )}
          </div>
        )}
        {job.checklist.length > 0 && (
        <div className="rounded-lg border border-border bg-white p-5 sm:p-6" data-testid="checklist-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-royal-deep">{job.work_type_label} checklist</h2>
            <span className="text-sm font-semibold text-royal" data-testid="checklist-progress">{done}/{job.checklist.length}</span>
          </div>
          <Progress value={pct} className="mt-3 h-2" />
          <div className="mt-6 space-y-6">
            {sections.map((section) => (
              <div key={section}>
                <p className="text-[11px] uppercase tracking-wider text-gold font-semibold">{section}</p>
                <div className="mt-2 divide-y divide-border">
                  {job.checklist.map((t, i) =>
                    t.section !== section ? null : (
                      <label
                        key={`${t.section}-${t.task}`}
                        data-testid={`checklist-item-${i}`}
                        className="flex items-start gap-3 py-2.5 cursor-pointer group"
                      >
                        <Checkbox
                          data-testid={`checklist-checkbox-${i}`}
                          checked={t.done}
                          disabled={!editable || job.status === "completed"}
                          onCheckedChange={() => toggleTask(i)}
                          className="mt-0.5 data-[state=checked]:bg-royal data-[state=checked]:border-royal"
                        />
                        <span className={`text-sm ${t.done ? "text-muted-foreground line-through" : "text-royal-deep"}`}>{t.task}</span>
                      </label>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-white p-5" data-testid="job-photos-card">
            <h2 className="font-display text-lg font-semibold text-royal-deep">Condition photos</h2>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {(job.photos || []).map((p, i) => (
                <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="h-20 rounded-md overflow-hidden border border-border block" data-testid={`job-photo-${i}`}>
                  <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                </a>
              ))}
              {editable && (
                <button
                  data-testid="add-job-photo-btn"
                  onClick={() => fileRef.current?.click()}
                  className="h-20 rounded-md border border-dashed border-royal/30 flex flex-col items-center justify-center gap-1 text-royal hover:bg-royal/5 transition-colors duration-200"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  <span className="text-[10px]">Upload</span>
                </button>
              )}
              <input ref={fileRef} data-testid="job-photo-input" type="file" accept="image/*" multiple hidden onChange={addPhotos} />
            </div>
            {!job.photos?.length && !editable && <p className="text-sm text-muted-foreground mt-2">No photos yet.</p>}
          </div>

          <div className="rounded-lg border border-border bg-white p-5 mt-6" data-testid="job-activity-card">
            <h2 className="font-display text-lg font-semibold text-royal-deep">Activity history</h2>
            <div className="mt-3 space-y-2.5">
              {(job.activity || []).length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
              )}
              {(job.activity || []).map((a, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`activity-${i}`}>
                  <span className="rounded-full border border-royal/15 bg-royal/5 px-2 py-0.5 text-[11px] text-royal">
                    {label(a.action)}
                  </span>
                  <p className="text-xs text-royal-deep">{a.by_name}</p>
                  {a.detail && <p className="text-xs text-muted-foreground">— {a.detail}</p>}
                  <p className="ml-auto text-[11px] text-muted-foreground">
                    {new Date(a.at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-5">
            <h2 className="font-display text-lg font-semibold text-royal-deep">Notes</h2>
            {job.notes && (
              <div className="mt-3 rounded-md bg-secondary p-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Instructions</p>
                <p className="text-sm text-royal-deep mt-1">{job.notes}</p>
              </div>
            )}
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Completion notes</Label>
              <Textarea
                data-testid="completion-notes-input"
                value={notes}
                disabled={!editable}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="mt-1.5"
                placeholder="What did you observe at this property?"
              />
              {editable && (
                <Button
                  data-testid="save-notes-btn"
                  onClick={() => patch({ completion_notes: notes }, "Notes saved")}
                  variant="outline"
                  className="mt-3 w-full border-royal/30 text-royal hover:bg-royal/5"
                >
                  Save notes
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
